import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Chip } from '@mui/material';
import { FaSearch } from '@react-icons/all-files/fa/FaSearch';
import { FaEye } from '@react-icons/all-files/fa/FaEye';
import { FaInfoCircle } from '@react-icons/all-files/fa/FaInfoCircle';
import { useLocation, useSearchParams } from 'react-router-dom';
import { SORT } from '../../commons/Datagrid';

export const GRID_EMPRESA_COL_CLASS = 'workflow-config-empresa-col';
export const GRID_EMPRESA_COL_START_CLASS = 'workflow-config-empresa-col-start';
export const GRID_GROUP_ACTIVIDAD_CLASS = 'workflow-config-grid-group-actividad';
export const GRID_GROUP_EMPRESA_CLASS = 'workflow-config-grid-group-empresa';

export interface WorkflowActivitiesFetchParams {
  sort: string;
  filters: { filterName: string; filterValue: string }[];
}

export interface WorkflowActiveEmpresaGroupHeader {
  grupoAlias?: string;
  mainLine: string;
  fullTitle: string;
}

interface WorkflowFilterDef {
  label: string;
  name: string;
  single?: boolean;
}

interface WorkflowConfigActivitiesTableProps {
  rows: any[];
  loading: boolean;
  gridVersion: number;
  workflowName: string;
  activeEmpresaGroupHeader: WorkflowActiveEmpresaGroupHeader;
  onWorkflowSearch: () => void;
  disenoPersonalizadoEnabled?: boolean;
  disenoPersonalizadoDisabled?: boolean;
  onDisenoPersonalizado?: () => void;
  filters: WorkflowFilterDef[];
  onFetch: (params: WorkflowActivitiesFetchParams) => void | Promise<void>;
  renderSelectAllHeader: () => React.ReactNode;
  renderHabilitadoActividadHeader?: () => React.ReactNode;
  renderHabilitadoActividadCell?: (row: any) => React.ReactNode;
  renderHabilitadoEmpresaHeader?: () => React.ReactNode;
  renderHabilitadoEmpresaCell?: (row: any) => React.ReactNode;
  renderAsignarAContadorEmpresaHeader?: () => React.ReactNode;
  renderRequiereAutorizacionEmpresaHeader?: () => React.ReactNode;
  renderRequiereArchivoEmpresaHeader?: () => React.ReactNode;
  onOpenActivitiesVisibility?: () => void;
  hasHiddenActivities?: boolean;
  hiddenActivitiesCount?: number;
  renderActividadFilterPrefix?: () => React.ReactNode;
  renderSelectCell: (row: any) => React.ReactNode;
  renderActividadCell: (row: any) => React.ReactNode;
  renderCheckbox: (
    row: any,
    field:
      | 'habilitadoEmpresa'
      | 'asignarAContadorEmpresa'
      | 'requiereAutorizacionEmpresa'
      | 'requiereArchivoEmpresa',
  ) => React.ReactNode;
  renderUsuarioHeader: (label: string, field: 'usuario' | 'autoriza') => React.ReactNode;
  renderUsuarioCell: (row: any, field: 'usuario' | 'autoriza') => React.ReactNode;
}

const tableS: Record<string, React.CSSProperties> = {
  wrap: {
    width: '100%',
    border: '1px solid var(--crm-grid-border)',
    backgroundColor: 'var(--crm-grid-bg)',
    color: 'var(--crm-body-text)',
    borderRadius: 4,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    height: 'min(520px, calc(100vh - 420px))',
  },
  toolbar: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
    alignItems: 'center',
    padding: '8px 12px',
    borderBottom: '1px solid var(--crm-grid-border)',
    backgroundColor: 'var(--crm-grid-header-bg)',
    minHeight: 40,
  },
  scroll: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    tableLayout: 'fixed',
  },
  th: {
    padding: '8px 10px',
    textAlign: 'left',
    verticalAlign: 'top',
    fontWeight: 600,
    fontSize: 13,
    color: 'var(--crm-grid-header-text)',
    backgroundColor: 'var(--crm-grid-header-bg)',
    borderBottom: '1px solid var(--crm-grid-border)',
    position: 'sticky',
    top: 44,
    zIndex: 2,
  },
  thGroup: {
    padding: '10px 12px',
    textAlign: 'center',
    fontWeight: 700,
    fontSize: 13,
    borderBottom: '1px solid var(--crm-grid-border)',
    position: 'sticky',
    top: 0,
    zIndex: 3,
  },
  thGroupActividad: {
    backgroundColor: 'var(--crm-grid-header-bg)',
    color: 'var(--crm-grid-header-text)',
  },
  thGroupEmpresa: {
    backgroundColor: 'var(--crm-grid-empresa-section-header-bg)',
    color: 'var(--crm-grid-empresa-section-header-text)',
  },
  thGroupTitle: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  thGroupEmpresaTitle: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  thGroupEmpresaGrupo: {
    color: '#60a5fa',
    flexShrink: 0,
  },
  thGroupEmpresaSep: {
    flexShrink: 0,
  },
  thGroupEmpresaMain: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  workflowSearchBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 22,
    padding: 0,
    border: '1px solid var(--crm-border, #cbd5e1)',
    borderRadius: 6,
    background: 'var(--crm-surface, #fff)',
    color: 'var(--crm-text-secondary, #64748b)',
    cursor: 'pointer',
    flexShrink: 0,
  },
  disenoPersonalizadoBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2px 8px',
    marginLeft: 4,
    border: '1px solid #7c3aed',
    borderRadius: 6,
    background: '#7c3aed',
    color: '#fff',
    fontSize: 11,
    fontWeight: 600,
    lineHeight: 1.3,
    cursor: 'pointer',
    flexShrink: 0,
  },
  disenoPersonalizadoBtnDisabled: {
    border: '1px solid var(--crm-border, #cbd5e1)',
    background: 'var(--crm-surface-muted, #f1f5f9)',
    color: 'var(--crm-text-muted, #94a3b8)',
    cursor: 'not-allowed',
    opacity: 0.75,
  },
  idHeaderRow: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  },
  visibilityBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 22,
    padding: 0,
    border: '1px solid var(--crm-border, #cbd5e1)',
    borderRadius: 6,
    background: 'var(--crm-surface, #fff)',
    color: 'var(--crm-text-secondary, #64748b)',
    cursor: 'pointer',
    flexShrink: 0,
  },
  visibilityBtnActive: {
    color: '#10b981',
    borderColor: '#86efac',
  },
  thEmpresa: {
    backgroundColor: 'var(--crm-grid-empresa-section-header-bg)',
    color: 'var(--crm-grid-empresa-section-header-text)',
  },
  thEmpresaStart: {
    borderLeft: '4px solid var(--crm-text-secondary, #64748b)',
  },
  td: {
    padding: '8px 10px',
    verticalAlign: 'middle',
    fontSize: 13,
    color: 'var(--crm-body-text)',
    borderBottom: '1px solid var(--crm-grid-border)',
    overflow: 'hidden',
  },
  tdEmpresa: {
    backgroundColor: 'var(--crm-grid-empresa-section-bg)',
  },
  tdEmpresaStart: {
    borderLeft: '4px solid var(--crm-text-secondary, #64748b)',
  },
  sortBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: 0,
    border: 'none',
    background: 'transparent',
    color: 'inherit',
    font: 'inherit',
    fontWeight: 600,
    cursor: 'pointer',
  },
  filterInput: {
    flex: 1,
    minWidth: 0,
    padding: '6px 8px',
    fontSize: 12,
    border: '1px solid var(--crm-border)',
    borderRadius: 6,
    background: 'var(--crm-input-bg)',
    color: 'var(--crm-text-primary)',
    boxSizing: 'border-box',
  },
  filterHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    width: '100%',
  },
  empresaHeaderCheckbox: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    minWidth: 0,
    width: '100%',
  },
  empty: {
    padding: 24,
    textAlign: 'center',
    color: 'var(--crm-text-secondary)',
    fontSize: 13,
  },
  loadingOverlay: {
    padding: 24,
    textAlign: 'center',
    color: 'var(--crm-text-secondary)',
    fontSize: 13,
  },
  hiddenActivitiesInfo: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    flexShrink: 0,
    margin: '0 12px 8px',
    padding: '8px 10px',
    fontSize: 12,
    lineHeight: 1.4,
    color: 'var(--crm-form-warning-text, #92400e)',
    background: 'var(--crm-form-warning-bg, #fffbeb)',
    border: '1px solid var(--crm-form-warning-border, #fcd34d)',
    borderRadius: 8,
  },
  hiddenActivitiesInfoIcon: {
    flexShrink: 0,
    marginTop: 1,
    color: 'var(--crm-form-warning-text, #d97706)',
  },
  cellWrap: {
    minWidth: 0,
    width: '100%',
  },
  checkboxCellCenter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
};

const tableHoverCss = `
.workflow-config-activities-table tbody tr:hover td {
  background-color: var(--crm-grid-row-hover);
}
.workflow-config-activities-table tbody tr:hover td.${GRID_EMPRESA_COL_CLASS} {
  background-color: var(--crm-grid-empresa-section-bg-hover);
}
`;

function filterChipValueLabel(field: WorkflowFilterDef, value: string): string {
  return value;
}

export default function WorkflowConfigActivitiesTable({
  rows,
  loading,
  gridVersion,
  workflowName,
  activeEmpresaGroupHeader,
  onWorkflowSearch,
  disenoPersonalizadoEnabled = false,
  disenoPersonalizadoDisabled = false,
  onDisenoPersonalizado,
  filters,
  onFetch,
  renderSelectAllHeader,
  renderHabilitadoActividadHeader,
  renderHabilitadoActividadCell,
  renderHabilitadoEmpresaHeader,
  renderHabilitadoEmpresaCell,
  renderAsignarAContadorEmpresaHeader,
  renderRequiereAutorizacionEmpresaHeader,
  renderRequiereArchivoEmpresaHeader,
  onOpenActivitiesVisibility,
  hasHiddenActivities = false,
  hiddenActivitiesCount = 0,
  renderActividadFilterPrefix,
  renderSelectCell,
  renderActividadCell,
  renderCheckbox,
  renderUsuarioHeader,
  renderUsuarioCell,
}: WorkflowConfigActivitiesTableProps) {
  const pageLocation = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const sortParam = searchParams.get(SORT);
  const [initialField, initialOrder] = sortParam != null ? sortParam.split(',') : ['id', 'asc'];

  const [sortField, setSortField] = useState(initialField || 'id');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(initialOrder === 'desc' ? 'desc' : 'asc');
  const [activeFilters, setActiveFilters] = useState<{ field: WorkflowFilterDef; value: string }[]>(() =>
    filters.flatMap(f => {
      const values = searchParams.getAll(f.name).filter(v => v !== '');
      if (f.single) {
        const v = values.length > 0 ? values[values.length - 1] : null;
        return v != null ? [{ field: f, value: v }] : [];
      }
      return values.map(v => ({ field: f, value: v }));
    }),
  );
  const [inputValues, setInputValues] = useState<Record<string, string>>({});

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const urlSort = params.get(SORT);
    if (urlSort) {
      const [field, order] = urlSort.split(',');
      if (field) setSortField(field);
      if (order === 'asc' || order === 'desc') setSortOrder(order);
    }
    const filtersFromUrl = filters.flatMap(f => {
      const values = params.getAll(f.name).filter(v => v !== '');
      if (f.single) {
        const v = values.length > 0 ? values[values.length - 1] : null;
        return v != null ? [{ field: f, value: v }] : [];
      }
      return values.map(v => ({ field: f, value: v }));
    });
    setActiveFilters(filtersFromUrl);
  }, [pageLocation.search, filters]);

  const runFetch = useCallback(() => {
    void onFetch({
      sort: `${sortField},${sortOrder}`,
      filters: activeFilters.map(f => ({
        filterName: f.field.name,
        filterValue: f.value,
      })),
    });
  }, [onFetch, sortField, sortOrder, activeFilters]);

  useEffect(() => {
    runFetch();
  }, [runFetch, gridVersion]);

  const toggleSort = () => {
    const nextOrder = sortField === 'id' && sortOrder === 'asc' ? 'desc' : 'asc';
    const params = new URLSearchParams(searchParams);
    params.set(SORT, `id,${nextOrder}`);
    setSearchParams(params);
    setSortField('id');
    setSortOrder(nextOrder);
  };

  const handleFilterKeyDown = (field: WorkflowFilterDef, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const value = inputValues[field.name]?.trim();
    if (!value) return;
    const params = new URLSearchParams(searchParams);
    if (!params.getAll(field.name).includes(value)) params.append(field.name, value);
    setSearchParams(params);
    setInputValues(prev => ({ ...prev, [field.name]: '' }));
  };

  const handleRemoveFilter = (filter: { field: WorkflowFilterDef; value: string }) => {
    const params = new URLSearchParams(searchParams);
    const remaining = params.getAll(filter.field.name).filter(v => v !== filter.value);
    params.delete(filter.field.name);
    remaining.forEach(v => params.append(filter.field.name, v));
    setSearchParams(params);
  };

  const actividadFilter = filters.find(f => f.name === 'profaseActividad.descripcion');

  const colWidths = useMemo(() => ({
    selected: '4%',
    id: '6%',
    actividad: '24%',
    habilitado: '9%',
    habilitadoEmpresa: '11%',
    asignarAContadorEmpresa: '9%',
    usuario: '13%',
    requiereAutorizacionEmpresa: '9%',
    requiereArchivoEmpresa: '9%',
    autoriza: '10%',
  }), []);

  return (
    <div style={tableS.wrap} className="workflow-config-activities-table">
      <style>{tableHoverCss}</style>
      {activeFilters.length > 0 ? (
        <div style={tableS.toolbar}>
          {activeFilters.map(f => (
            <Chip
              key={`${f.field.name}:${f.value}`}
              label={`${f.field.label}: ${filterChipValueLabel(f.field, f.value)}`}
              onDelete={() => handleRemoveFilter(f)}
              color="primary"
              size="small"
            />
          ))}
        </div>
      ) : null}

      {hiddenActivitiesCount > 0 ? (
        <div style={tableS.hiddenActivitiesInfo} role="status">
          <FaInfoCircle size={14} style={tableS.hiddenActivitiesInfoIcon} aria-hidden />
          <span>
            Hay {hiddenActivitiesCount} actividad{hiddenActivitiesCount === 1 ? '' : 'es'}
            {' '}que se encuentran ocultas y bloqueadas
          </span>
        </div>
      ) : null}

      <div style={tableS.scroll}>
        <table style={tableS.table}>
          <colgroup>
            <col style={{ width: colWidths.selected }} />
            <col style={{ width: colWidths.id }} />
            <col style={{ width: colWidths.actividad }} />
            <col style={{ width: colWidths.habilitado }} />
            <col style={{ width: colWidths.habilitadoEmpresa }} />
            <col style={{ width: colWidths.asignarAContadorEmpresa }} />
            <col style={{ width: colWidths.usuario }} />
            <col style={{ width: colWidths.requiereAutorizacionEmpresa }} />
            <col style={{ width: colWidths.autoriza }} />
            <col style={{ width: colWidths.requiereArchivoEmpresa }} />
          </colgroup>
          <thead>
            <tr>
              <th
                colSpan={4}
                style={{ ...tableS.thGroup, ...tableS.thGroupActividad }}
                className={GRID_GROUP_ACTIVIDAD_CLASS}
              >
                <span style={tableS.thGroupTitle}>
                  {workflowName}
                  <button
                    type="button"
                    style={tableS.workflowSearchBtn}
                    title="Buscar workflow"
                    aria-label="Buscar workflow"
                    onClick={onWorkflowSearch}
                  >
                    <FaSearch size={12} />
                  </button>
                  {disenoPersonalizadoEnabled && onDisenoPersonalizado ? (
                    <button
                      type="button"
                      style={{
                        ...tableS.disenoPersonalizadoBtn,
                        ...(disenoPersonalizadoDisabled ? tableS.disenoPersonalizadoBtnDisabled : {}),
                      }}
                      title={
                        disenoPersonalizadoDisabled
                          ? 'Salga del modo Editar para abrir el diseño personalizado'
                          : 'Abrir diseño personalizado de la empresa seleccionada'
                      }
                      aria-label="Diseño Personalizado"
                      disabled={disenoPersonalizadoDisabled}
                      onClick={onDisenoPersonalizado}
                    >
                      Diseño Personalizado
                    </button>
                  ) : null}
                </span>
              </th>
              <th
                colSpan={6}
                style={{ ...tableS.thGroup, ...tableS.thGroupEmpresa }}
                className={GRID_GROUP_EMPRESA_CLASS}
              >
                <span
                  style={{ ...tableS.thGroupTitle, ...tableS.thGroupEmpresaTitle }}
                  title={activeEmpresaGroupHeader.fullTitle}
                >
                  {activeEmpresaGroupHeader.grupoAlias ? (
                    <>
                      <span style={tableS.thGroupEmpresaGrupo}>
                        {activeEmpresaGroupHeader.grupoAlias}
                      </span>
                      <span style={tableS.thGroupEmpresaSep}> - </span>
                    </>
                  ) : null}
                  <span style={tableS.thGroupEmpresaMain}>
                    {activeEmpresaGroupHeader.mainLine}
                  </span>
                </span>
              </th>
            </tr>
            <tr>
              <th style={tableS.th}>
                <div style={tableS.checkboxCellCenter}>{renderSelectAllHeader()}</div>
              </th>
              <th style={tableS.th}>
                <span style={tableS.idHeaderRow}>
                  {onOpenActivitiesVisibility ? (
                    <button
                      type="button"
                      style={{
                        ...tableS.visibilityBtn,
                        ...(hasHiddenActivities ? tableS.visibilityBtnActive : {}),
                      }}
                      title="Visibilidad de actividades"
                      aria-label="Visibilidad de actividades"
                      onClick={onOpenActivitiesVisibility}
                    >
                      <FaEye size={12} />
                    </button>
                  ) : null}
                  <button type="button" style={tableS.sortBtn} onClick={toggleSort}>
                    ID
                    {sortField === 'id' ? (sortOrder === 'asc' ? ' ↑' : ' ↓') : null}
                  </button>
                </span>
              </th>
              <th style={tableS.th}>
                <div style={tableS.filterHeaderRow}>
                  {renderActividadFilterPrefix?.()}
                  {actividadFilter ? (
                    <input
                      type="text"
                      className="profase-datagrid-filter-control"
                      style={tableS.filterInput}
                      value={inputValues[actividadFilter.name] ?? ''}
                      placeholder="Filtrar…"
                      onChange={e => setInputValues(prev => ({
                        ...prev,
                        [actividadFilter.name]: e.target.value,
                      }))}
                      onKeyDown={e => handleFilterKeyDown(actividadFilter, e)}
                    />
                  ) : null}
                </div>
              </th>
              <th style={tableS.th}>
                <div style={tableS.empresaHeaderCheckbox}>
                  {renderHabilitadoActividadHeader?.() ?? 'Habilitado'}
                </div>
              </th>
              <th
                style={{ ...tableS.th, ...tableS.thEmpresa, ...tableS.thEmpresaStart }}
                className={`${GRID_EMPRESA_COL_CLASS} ${GRID_EMPRESA_COL_START_CLASS}`}
              >
                <div style={tableS.empresaHeaderCheckbox}>
                  {renderHabilitadoEmpresaHeader?.() ?? 'Habilitada'}
                </div>
              </th>
              <th style={{ ...tableS.th, ...tableS.thEmpresa }} className={GRID_EMPRESA_COL_CLASS}>
                <div style={tableS.empresaHeaderCheckbox}>
                  {renderAsignarAContadorEmpresaHeader?.() ?? 'Asig. contador'}
                </div>
              </th>
              <th style={{ ...tableS.th, ...tableS.thEmpresa }} className={GRID_EMPRESA_COL_CLASS}>
                {renderUsuarioHeader('Usuario', 'usuario')}
              </th>
              <th style={{ ...tableS.th, ...tableS.thEmpresa }} className={GRID_EMPRESA_COL_CLASS}>
                <div style={tableS.empresaHeaderCheckbox}>
                  {renderRequiereAutorizacionEmpresaHeader?.() ?? 'Req. Autoriz.'}
                </div>
              </th>
              <th style={{ ...tableS.th, ...tableS.thEmpresa }} className={GRID_EMPRESA_COL_CLASS}>
                {renderUsuarioHeader('Autoriza (usuario)', 'autoriza')}
              </th>
              <th style={{ ...tableS.th, ...tableS.thEmpresa }} className={GRID_EMPRESA_COL_CLASS}>
                <div style={tableS.empresaHeaderCheckbox}>
                  {renderRequiereArchivoEmpresaHeader?.() ?? 'Req. Archivo'}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={10} style={tableS.loadingOverlay}>Cargando…</td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={10} style={tableS.empty}>Sin Registro</td>
              </tr>
            ) : rows.map(row => (
              <tr
                key={row.id ?? JSON.stringify(row)}
                className="workflow-config-activities-row"
              >
                <td style={tableS.td}>
                  <div style={tableS.checkboxCellCenter}>{renderSelectCell(row)}</div>
                </td>
                <td style={tableS.td}>{row.id}</td>
                <td style={tableS.td}>
                  <div style={tableS.cellWrap}>{renderActividadCell(row)}</div>
                </td>
                <td style={tableS.td}>
                  <div style={tableS.cellWrap}>
                    {renderHabilitadoActividadCell?.(row)}
                  </div>
                </td>
                <td
                  style={{ ...tableS.td, ...tableS.tdEmpresa, ...tableS.tdEmpresaStart }}
                  className={`${GRID_EMPRESA_COL_CLASS} ${GRID_EMPRESA_COL_START_CLASS}`}
                >
                  <div style={tableS.cellWrap}>
                    {renderHabilitadoEmpresaCell?.(row)}
                  </div>
                </td>
                <td style={{ ...tableS.td, ...tableS.tdEmpresa }} className={GRID_EMPRESA_COL_CLASS}>
                  <div style={tableS.checkboxCellCenter}>
                    {renderCheckbox(row, 'asignarAContadorEmpresa')}
                  </div>
                </td>
                <td style={{ ...tableS.td, ...tableS.tdEmpresa }} className={GRID_EMPRESA_COL_CLASS}>
                  <div style={tableS.cellWrap}>{renderUsuarioCell(row, 'usuario')}</div>
                </td>
                <td style={{ ...tableS.td, ...tableS.tdEmpresa }} className={GRID_EMPRESA_COL_CLASS}>
                  <div style={tableS.checkboxCellCenter}>
                    {renderCheckbox(row, 'requiereAutorizacionEmpresa')}
                  </div>
                </td>
                <td style={{ ...tableS.td, ...tableS.tdEmpresa }} className={GRID_EMPRESA_COL_CLASS}>
                  <div style={tableS.cellWrap}>{renderUsuarioCell(row, 'autoriza')}</div>
                </td>
                <td style={{ ...tableS.td, ...tableS.tdEmpresa }} className={GRID_EMPRESA_COL_CLASS}>
                  <div style={tableS.checkboxCellCenter}>
                    {renderCheckbox(row, 'requiereArchivoEmpresa')}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
